import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import i18n from '@/i18n';
import { useAuthStore } from '@/store/authStore';
import type { Project, Scene, Hotspot, ApiResponse, BillingInfo, Plan } from '@my360/shared';

// Billing info (storage usage, plan, subscription)
export function useBillingInfo() {
  // Gated on the session: these run from globally-mounted components
  // (GlobalUpgradeFlow), whose `if (!isAuthenticated) return null` guard
  // sits AFTER the hooks — hooks always run, so without this the public
  // pages fired /billing and logged 401s on every visit.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return useQuery({
    queryKey: ['billing'],
    queryFn: () => api.request<ApiResponse<BillingInfo>>('/billing'),
    staleTime: 1000 * 30, // 30s — usage updates frequently
    enabled: isAuthenticated,
  });
}

// Plans catalog (used by BillingQuickPanel + Settings to power the upgrade
// CTA without each consumer fetching independently). Plans rarely change so
// we cache aggressively (10min) — Settings already invalidates on relevant
// events.
export function usePlans() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: () =>
      api.request<ApiResponse<(Plan & { priceFormatted: string })[]>>('/billing/plans'),
    staleTime: 1000 * 60 * 10,
    enabled: isAuthenticated,
  });
}

// Upgrade an ACTIVE paid subscription to a higher plan — switches the plan
// and price on Asaas + DB instantly (no new checkout). Callers without an
// active paid sub must keep using the checkout flow.
export function useUpgradePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (plan: string) =>
      api.request<ApiResponse<{ oldPlan: string; newPlan: string; message: string }>>(
        '/billing/upgrade',
        { method: 'POST', body: { plan } },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}

// Coupon / invite-code preview — validates the code for the current tenant
// against a target plan (`plan`) OR storage pack (`pack`) and returns the
// overridden price for display. The authoritative validation + redemption
// happens inside /billing/checkout or /storage-packs/checkout.
export interface CouponPreview {
  valid: boolean;
  priceCents: number;
  description: string | null;
}

export function useValidateCoupon() {
  return useMutation({
    mutationFn: ({ code, plan, pack }: { code: string; plan?: string; pack?: string }) => {
      const target = pack
        ? `pack=${encodeURIComponent(pack)}`
        : `plan=${encodeURIComponent(plan ?? '')}`;
      return api.request<ApiResponse<CouponPreview>>(
        `/billing/coupon/${encodeURIComponent(code.trim().toUpperCase())}?${target}`,
      );
    },
  });
}

// Projects
export function useProjects() {
  return useQuery({
    queryKey: ['projects'],
    queryFn: () => api.request<ApiResponse<Project[]>>('/projects'),
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: ['projects', id],
    queryFn: () =>
      api.request<ApiResponse<Project & { scenes: (Scene & { hotspots: Hotspot[] })[] }>>(
        `/projects/${id}`
      ),
    enabled: !!id,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { title: string; description?: string }) =>
      api.request<ApiResponse<Project>>('/projects', { method: 'POST', body: data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<Project>) =>
      api.request<ApiResponse<Project>>(`/projects/${id}`, { method: 'PATCH', body: data }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.id] });
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.request<ApiResponse<void>>(`/projects/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function usePublishProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.request<ApiResponse<Project>>(`/projects/${id}/publish`, { method: 'POST' }),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', id] });
    },
  });
}

export function useUnpublishProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.request<ApiResponse<Project>>(`/projects/${id}/unpublish`, { method: 'POST' }),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', id] });
    },
  });
}

// Scenes
export function useCreateScene() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      projectId: string;
      title: string;
      imageUrl: string | null;
      thumbnailUrl?: string;
      videoUrl?: string | null;
      isVideo?: boolean;
      videoDuration?: number | null;
    }) => api.request<ApiResponse<Scene>>('/scenes', { method: 'POST', body: data }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
    },
  });
}

export function useUpdateScene() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      projectId,
      ...data
    }: { id: string; projectId: string } & Partial<Scene>) =>
      api.request<ApiResponse<Scene>>(`/scenes/${id}`, { method: 'PATCH', body: data }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
    },
  });
}

export function useDeleteScene() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, projectId: _projectId }: { id: string; projectId: string }) =>
      api.request<ApiResponse<void>>(`/scenes/${id}`, { method: 'DELETE' }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
    },
  });
}

export function useReorderScenes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { projectId: string; sceneIds: string[] }) =>
      api.request<ApiResponse<void>>('/scenes/reorder', { method: 'POST', body: data }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
    },
  });
}

export function useCloneScene() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      projectId,
      title,
    }: {
      id: string;
      projectId: string;
      /** Title for the copy. Omit to let it be derived from the source scene. */
      title?: string;
    }) => {
      // The API has no i18n — without a title it falls back to the English
      // `Copy of X`. Derive the localized one from the cached project so a
      // PT-BR user gets "Cópia de Cozinha | 1º andar".
      const cached = queryClient.getQueryData<
        ApiResponse<Project & { scenes: (Scene & { hotspots: Hotspot[] })[] }>
      >(['projects', projectId]);
      const sourceTitle = cached?.data?.scenes?.find((s) => s.id === id)?.title;
      const cloneTitle =
        title ?? (sourceTitle ? i18n.t('editorUi:scene.copyOf', { title: sourceTitle }) : undefined);

      return api.request<ApiResponse<Scene & { hotspots: Hotspot[] }>>(`/scenes/${id}/clone`, {
        method: 'POST',
        body: cloneTitle ? { title: cloneTitle } : undefined,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
    },
  });
}


// Hotspots
export function useCreateHotspot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      data: {
        sceneId: string;
        projectId: string;
      } & Omit<Hotspot, 'id' | 'createdAt'>
    ) => {
      // Remove projectId from body - backend doesn't expect it
      const { projectId, ...body } = data;
      return api.request<ApiResponse<Hotspot>>('/hotspots', { method: 'POST', body });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
    },
  });
}

export function useUpdateHotspot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      projectId,
      ...data
    }: { id: string; projectId: string } & Partial<Hotspot>) =>
      api.request<ApiResponse<Hotspot>>(`/hotspots/${id}`, { method: 'PATCH', body: data }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
    },
  });
}

export function useDeleteHotspot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, projectId: _projectId }: { id: string; projectId: string }) =>
      api.request<ApiResponse<void>>(`/hotspots/${id}`, { method: 'DELETE' }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
    },
  });
}

// Upload — delegates to the unified /sign+/finalize pipeline in lib/upload.ts.
// HEIC sources are transparently transcoded to JPEG q95 in the browser so the
// caller doesn't need to know.
import { uploadPanorama } from '@/lib/upload';

export function useUploadImage() {
  return useMutation({
    mutationFn: (file: File) => uploadPanorama(file),
  });
}

// Orphan cleanup — deletes finalized assets that never got attached to a row
// (e.g. the upload succeeded but the follow-up createScene hit the plan limit)
// and refunds the tenant's storage quota. Max 20 URLs per call, server-side.
export function useDeleteOrphanAssets() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (urls: string[]) =>
      api.request<{ success: boolean; bytesFreed: number }>('/upload/asset', {
        method: 'DELETE',
        body: { urls },
      }),
    onSuccess: () => {
      // Storage usage changed — refresh the quota chips/guards.
      queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}

// Shared shape for public tour responses (gate or full)
export type PublicTourPayload =
  | {
      requiresPassword: true;
      id: string;
      shareId: string;
      title: string;
      description: string | null;
      thumbnailUrl: string | null;
      branding: { name: string | null; logo: string | null };
    }
  | {
      requiresPassword: false;
      id: string;
      shareId: string;
      title: string;
      description: string | null;
      settings: Project['settings'];
      thumbnailUrl: string | null;
      scenes: (Scene & { hotspots: Hotspot[] })[];
      branding: { name: string | null; logo: string | null };
    };

// ─── Unified billing history (Asaas as source of truth) ──────────

export interface BillingHistoryItem {
  kind: 'plan' | 'pack';
  sku: string;
  skuName: string;
  subscriptionId: string;
  paymentId: string;
  value: number;
  valueFormatted: string;
  status: string;
  dueDate: string;
  paymentDate: string | null;
  billingType: string;
  invoiceUrl: string | null;
  bankSlipUrl: string | null;
}

export interface BillingHistoryTotals {
  count: number;
  paid: number;
  pending: number;
  overdue: number;
  totalPaidCents: number;
}

export function useBillingHistory() {
  return useQuery({
    queryKey: ['billing', 'history'],
    queryFn: () =>
      api.request<ApiResponse<{ items: BillingHistoryItem[]; totals: BillingHistoryTotals }>>(
        '/billing/history',
      ),
    staleTime: 1000 * 30,
  });
}

// ─── Storage packs ──────────────────────────────────────────────────

export interface StoragePack {
  id: string;
  slug: string;
  name: string;
  bonusBytes: number;
  priceCents: number;
  active: boolean;
}

export interface TenantStoragePack {
  id: string;
  packSlug: string;
  status: 'pending' | 'active' | 'overdue' | 'cancelled';
  activeUntil: string | null;
  asaasSubscriptionId: string | null;
  createdAt: string;
  packName: string | null;
  bonusBytes: number | null;
  priceCents: number | null;
}

export function useStoragePacks() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return useQuery({
    queryKey: ['storage-packs'],
    queryFn: () => api.request<ApiResponse<StoragePack[]>>('/storage-packs'),
    enabled: isAuthenticated,
  });
}

export function useMyStoragePacks() {
  return useQuery({
    queryKey: ['storage-packs', 'my'],
    queryFn: () => api.request<ApiResponse<TenantStoragePack[]>>('/storage-packs/my'),
  });
}

export interface StoragePackCheckoutBody {
  packSlug: string;
  billingType: 'PIX' | 'BOLETO' | 'CREDIT_CARD';
  name: string;
  email: string;
  cpfCnpj: string;
  phone?: string;
  // Coupon / invite code — must list 'pack:<slug>' in its applies_to
  couponCode?: string;
}

export function useBuyStoragePack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: StoragePackCheckoutBody) =>
      api.request<ApiResponse<{ id: string; packSlug: string; asaasSubscriptionId: string; status: string }>>(
        '/storage-packs/checkout',
        { method: 'POST', body: data },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storage-packs', 'my'] });
      queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}

export function useCancelStoragePack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.request<ApiResponse<{ id: string; status: string }>>(`/storage-packs/${id}/cancel`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storage-packs', 'my'] });
      queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}

export function useSwapStoragePack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, newPackSlug, billingType, creditCardToken, name, email, cpfCnpj, phone }: {
      id: string;
      newPackSlug: string;
      billingType: 'PIX' | 'BOLETO' | 'CREDIT_CARD';
      // Credit card swap — token + holder info (same shape as checkout)
      creditCardToken?: string;
      name?: string;
      email?: string;
      cpfCnpj?: string;
      phone?: string;
    }) =>
      api.request<ApiResponse<{ id: string; packSlug: string; status: string; replacedPackId: string }>>(
        `/storage-packs/${id}/swap`,
        { method: 'POST', body: { newPackSlug, billingType, creditCardToken, name, email, cpfCnpj, phone } },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storage-packs', 'my'] });
      queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}

// Public tour (by shareId) — password is sent via x-tour-password header so it
// doesn't end up in URL params, browser history or proxy logs.
export function usePublicTour(shareId: string, password?: string) {
  return useQuery({
    queryKey: ['public-tour', shareId, password ?? ''],
    queryFn: () =>
      api.request<ApiResponse<PublicTourPayload>>(`/public/tour/${shareId}`, {
        headers: password ? { 'x-tour-password': password } : undefined,
      }),
    enabled: !!shareId,
  });
}

// Public tour (by custom domain)
export function usePublicTourByDomain(domain: string, password?: string) {
  return useQuery({
    queryKey: ['public-tour-domain', domain, password ?? ''],
    queryFn: () =>
      api.request<ApiResponse<PublicTourPayload>>(`/public/domain/${domain}`, {
        headers: password ? { 'x-tour-password': password } : undefined,
      }),
    enabled: !!domain,
  });
}

// Verify tour password (returns whether the supplied password is correct)
export function useVerifyTourPassword(shareId: string) {
  return useMutation({
    mutationFn: (password: string) =>
      api.request<ApiResponse<{ verified: boolean }>>(
        `/public/tour/${shareId}/verify`,
        { method: 'POST', body: { password } },
      ),
  });
}

// Google Street View
export function useGsvAuthStatus() {
  return useQuery({
    queryKey: ['gsv', 'auth-status'],
    queryFn: () =>
      api.request<
        ApiResponse<{
          connected: boolean;
          email: string | null;
          expiresAt: string | null;
          needsRefresh?: boolean;
        }>
      >('/gsv/auth/status'),
  });
}

export function useGsvAuthUrl() {
  return useMutation({
    mutationFn: (projectId?: string) =>
      api.request<ApiResponse<{ authUrl: string }>>('/gsv/auth/url', {
        method: 'POST',
        body: { projectId },
      }),
  });
}

export function useGsvDisconnect() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api.request<ApiResponse<void>>('/gsv/auth/disconnect', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gsv', 'auth-status'] });
    },
  });
}

export function useGsvPublicationStatus(sceneId: string) {
  return useQuery({
    queryKey: ['gsv', 'status', sceneId],
    queryFn: () =>
      api.request<ApiResponse<{
        id: string;
        sceneId: string;
        gsvPhotoId: string | null;
        status: 'pending' | 'uploading' | 'published' | 'failed';
        gsvUrl: string | null;
        error: string | null;
        publishedAt: string | null;
      } | null>>(`/gsv/status/${sceneId}`),
    enabled: !!sceneId,
  });
}

export function useUpdateSceneLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sceneId,
      latitude,
      longitude,
      heading,
    }: {
      sceneId: string;
      latitude: number | null;
      longitude: number | null;
      heading: number | null;
    }) =>
      api.request<ApiResponse<Scene>>(`/gsv/scene/${sceneId}/location`, {
        method: 'PATCH',
        body: { latitude, longitude, heading },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useGsvPublishScene() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sceneId: string) =>
      api.request<ApiResponse<unknown>>(`/gsv/publish/${sceneId}`, { method: 'POST' }),
    onSuccess: (_, sceneId) => {
      queryClient.invalidateQueries({ queryKey: ['gsv', 'status', sceneId] });
    },
  });
}

// Custom Domain Management
export function useDomainConfig(projectId: string) {
  return useQuery({
    queryKey: ['domain', projectId],
    queryFn: () =>
      api.request<
        ApiResponse<{
          customDomain: string | null;
          domainVerified: boolean;
          cnameTarget: string;
        }>
      >(`/domains/${projectId}`),
    enabled: !!projectId,
  });
}

export function useSetCustomDomain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, domain }: { projectId: string; domain: string }) =>
      api.request<
        ApiResponse<{
          customDomain: string | null;
          domainVerified: boolean;
          cnameTarget: string;
        }>
      >(`/domains/${projectId}`, { method: 'POST', body: { domain } }),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['domain', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
    },
  });
}

export function useVerifyDomain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectId: string) =>
      api.request<
        ApiResponse<{
          verified: boolean;
          cnameFound: boolean;
          cnameValue: string | null;
          txtFound: boolean;
          txtValue: string | null;
          error: string | null;
          domain: string;
        }>
      >(`/domains/${projectId}/verify`, { method: 'POST' }),
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: ['domain', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
    },
  });
}

export function useRemoveCustomDomain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectId: string) =>
      api.request<ApiResponse<void>>(`/domains/${projectId}`, { method: 'DELETE' }),
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: ['domain', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
    },
  });
}

// Analytics — full response shape returned by GET /projects/:id/analytics.
// Fields added in the analytics-funcional rollout: leadSubmits,
// avgDwellMs, conversionRate, activeVisitors (realtime via WS hint),
// osBreakdown, countryBreakdown, referrerBreakdown, topHotspots.
export type AnalyticsPeriod = '24h' | '7d' | '30d' | '90d' | 'all';

export interface AnalyticsData {
  period: AnalyticsPeriod;
  totalViews: number;
  uniqueVisitors: number;
  hotspotClicks: number;
  leadSubmits: number;
  avgDwellMs: number;
  conversionRate: number; // 0..1
  activeVisitors: number;
  viewsByDay: Array<{ date: string; views: number }>;
  deviceBreakdown: Array<{ device: string; count: number }>;
  osBreakdown: Array<{ os: string; count: number }>;
  countryBreakdown: Array<{ country: string | null; count: number }>;
  referrerBreakdown: Array<{ referrer: string | null; count: number }>;
  topScenes: Array<{ sceneId: string; sceneTitle: string; views: number }>;
  topHotspots: Array<{
    hotspotId: string;
    hotspotType: string;
    title: string;
    clicks: number;
  }>;
}

export interface HeatmapData {
  binsX: number;
  binsY: number;
  gaze: Array<{ x: number; y: number; count: number }>;
  clicks: Array<{ hotspotId: string; count: number }>;
}

export function useProjectAnalytics(projectId: string, period: AnalyticsPeriod = '30d') {
  return useQuery({
    queryKey: ['analytics', projectId, period],
    queryFn: () =>
      api.request<ApiResponse<AnalyticsData>>(`/projects/${projectId}/analytics?period=${period}`),
    enabled: !!projectId,
    // Keep fresh-ish for the dashboard but don't hammer the API: the
    // ingestion is async, freshness within a minute is fine.
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

export function useSceneHeatmap(projectId: string, sceneId: string | null, period: AnalyticsPeriod = '30d') {
  return useQuery({
    queryKey: ['heatmap', projectId, sceneId, period],
    queryFn: () =>
      api.request<ApiResponse<HeatmapData>>(
        `/projects/${projectId}/scenes/${sceneId}/heatmap?period=${period}`,
      ),
    enabled: !!projectId && !!sceneId,
    staleTime: 60_000,
  });
}

// Tenant-wide overview — aggregated KPIs + per-project rollup. Powers
// the AnalyticsOverview page (cross-tour rollup view).
export interface TenantAnalyticsOverview {
  period: AnalyticsPeriod;
  totals: {
    totalViews: number;
    uniqueVisitors: number;
    hotspotClicks: number;
    leadSubmits: number;
    avgDwellMs: number;
    conversionRate: number;
    ctr: number;
  };
  viewsByDay: Array<{ date: string; views: number }>;
  perProject: Array<{
    projectId: string;
    title: string;
    published: boolean;
    thumbnailUrl: string | null;
    views: number;
    clicks: number;
    leads: number;
    uniques: number;
    sparkline: number[]; // 14 normalised points (last 14 days of views)
  }>;
  topHotspots: Array<{
    projectId: string;
    projectTitle: string;
    hotspotId: string;
    hotspotType: string;
    title: string;
    clicks: number;
  }>;
  deviceBreakdown: Array<{ device: string; count: number }>;
  countryBreakdown: Array<{ country: string | null; count: number }>;
  videoInsights: Array<{
    sceneId: string;
    title: string;
    projectId: string;
    projectTitle: string;
    duration: number;
    views: number;
    hotspotClicks: number;
  }>;
}

export function useTenantAnalyticsOverview(period: AnalyticsPeriod = '30d') {
  return useQuery({
    queryKey: ['tenant-analytics-overview', period],
    queryFn: () =>
      api.request<ApiResponse<TenantAnalyticsOverview>>(
        `/tenant/analytics/overview?period=${period}`,
      ),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

// Email verification — nothing is gated on it, it only drives the "confirm
// your e-mail" nudge in Settings. The signup token expires in 24h, so the
// resend below is the only way back for users who missed it.
export function useEmailVerificationStatus(enabled = true) {
  return useQuery({
    queryKey: ['auth', 'verification-status'],
    queryFn: () =>
      api.request<ApiResponse<{ emailVerified: boolean; email: string }>>(
        '/auth/verification-status',
      ),
    enabled,
    staleTime: 1000 * 60 * 5,
  });
}

export function useResendVerificationEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api.request<ApiResponse<{ alreadyVerified: boolean; sent: boolean; email?: string }>>(
        '/auth/resend-verification',
        { method: 'POST' },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'verification-status'] });
    },
  });
}

// Support request — e-mails the support inbox (there is no ticket system;
// the answer comes back by e-mail). Rate-limited server-side.
export function useSendSupportRequest() {
  return useMutation({
    mutationFn: ({ subject, message }: { subject: string; message: string }) =>
      api.request<ApiResponse<{ sentTo: string; copyTo: string }>>('/support/request', {
        method: 'POST',
        body: { subject, message },
      }),
  });
}

// ── Platform admin (all gated by /admin/* — platform staff only) ───────────
// Sensitive operations (suspension, plan grant, refund, impersonation,
// platform-admin flag) are audit-logged server-side; every mutation below
// invalidates the audit feed so the trail updates immediately.

export interface AdminAuditEntry {
  id: string;
  action: string;
  targetType: string;
  targetId: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  actorUserId: string | null;
  actorEmail: string | null;
  actorName: string | null;
}

export function useAdminAudit(enabled = true, limit = 50) {
  return useQuery({
    queryKey: ['admin', 'audit', limit],
    queryFn: () => api.request<ApiResponse<AdminAuditEntry[]>>(`/admin/audit?limit=${limit}`),
    enabled,
    staleTime: 15_000,
  });
}

export function useSetPlatformAdmin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; enabled: boolean }) =>
      api.request<ApiResponse<{ id: string; email: string; isPlatformAdmin: boolean }>>(
        `/admin/users/${userId}/platform-admin`,
        { method: 'POST', body: { enabled } },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

export interface ImpersonationSession {
  accessToken: string;
  expiresInSeconds: number;
  user: { id: string; email: string; tenantId: string };
}

// Issues a SHORT-LIVED access token for the target user (no refresh token is
// ever returned — the support session simply expires).
export function useImpersonateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) =>
      api.request<ApiResponse<ImpersonationSession>>(`/admin/users/${userId}/impersonate`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

export function useSuspendTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenantId, reason }: { tenantId: string; reason?: string }) =>
      api.request<ApiResponse<{ id: string; suspendedAt: string; unpublishedProjects: number }>>(
        `/admin/tenants/${tenantId}/suspend`,
        { method: 'POST', body: { reason } },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

export function useUnsuspendTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tenantId: string) =>
      api.request<ApiResponse<{ id: string; republishedProjects: number }>>(
        `/admin/tenants/${tenantId}/unsuspend`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

// Manual plan grant — writes the same fields a paid activation writes
// (plan + white-label + subscription period), with an optional expiry.
export function useGrantTenantPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      plan,
      expiresAt,
      note,
    }: {
      tenantId: string;
      plan: string;
      expiresAt?: string | null;
      note?: string;
    }) =>
      api.request<ApiResponse<{ id: string; plan: string; whiteLabel: boolean; expiresAt: string | null }>>(
        `/admin/tenants/${tenantId}/plan`,
        { method: 'POST', body: { plan, expiresAt: expiresAt ?? null, note } },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

// Refunds on Asaas AND reverses the sponsor commission of that payment.
export function useRefundPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      paymentId,
      valueCents,
      description,
    }: {
      paymentId: string;
      valueCents?: number;
      description?: string;
    }) =>
      api.request<ApiResponse<{ id: string; status: string; value: number }>>(
        `/admin/payments/${paymentId}/refund`,
        { method: 'POST', body: { valueCents, description } },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

export interface AdminIncident {
  id: string;
  title: string;
  body: string;
  impact: 'none' | 'minor' | 'major' | 'critical';
  state: 'investigating' | 'identified' | 'monitoring' | 'resolved';
  affectedServices: string[];
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
}

export function useAdminIncidents(enabled = true) {
  return useQuery({
    queryKey: ['admin', 'incidents'],
    queryFn: () => api.request<ApiResponse<AdminIncident[]>>('/admin/status/incidents'),
    enabled,
    staleTime: 15_000,
  });
}

export function useCreateIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: {
      title: string;
      body: string;
      impact: AdminIncident['impact'];
      affectedServices?: string[];
    }) =>
      api.request<ApiResponse<AdminIncident>>('/admin/status/incidents', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'incidents'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

export function useUpdateIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Partial<Pick<AdminIncident, 'title' | 'body' | 'impact' | 'state'>>;
    }) =>
      api.request<ApiResponse<AdminIncident>>(`/admin/status/incidents/${id}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'incidents'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

// ── Showcase (curated demo tours on the landing page) ────────────────────

/** Public landing-page card. Facts only — no fabricated metrics. */
export interface ShowcaseTour {
  shareId: string;
  title: string;
  description: string | null;
  thumbnailUrl: string | null;
  sceneCount: number;
}

/**
 * Public, unauthenticated. Landing renders the real tours when this returns
 * any, and falls back to the illustrative cards otherwise — so an empty list
 * (or a failed request) is a normal, non-blocking state, never an error UI.
 */
export function useShowcaseTours() {
  return useQuery({
    queryKey: ['public', 'showcase'],
    queryFn: () => api.request<ApiResponse<ShowcaseTour[]>>('/public/showcase'),
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
}

/** Admin row for the showcase picker. */
export interface AdminShowcaseProject {
  id: string;
  title: string;
  shareId: string;
  thumbnailUrl: string | null;
  featured: boolean;
  published: boolean;
  updatedAt: string;
  sceneCount: number;
  tenantName: string | null;
}

export function useAdminProjects(search: string, enabled = true) {
  return useQuery({
    queryKey: ['admin', 'projects', search],
    queryFn: () =>
      api.request<ApiResponse<AdminShowcaseProject[]>>(
        `/admin/projects?limit=50&search=${encodeURIComponent(search)}`,
      ),
    enabled,
    staleTime: 30_000,
  });
}

export function useSetProjectFeatured() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, featured }: { id: string; featured: boolean }) =>
      api.request<ApiResponse<{ id: string; featured: boolean }>>(
        `/admin/projects/${id}/featured`,
        { method: 'PATCH', body: { featured } },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'projects'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'audit'] });
      queryClient.invalidateQueries({ queryKey: ['public', 'showcase'] });
    },
  });
}

// ── Sign in with Google ──────────────────────────────────────────────────
// Whether the backend has GOOGLE_CLIENT_ID/SECRET configured. The feature is
// NOT enabled in production today, so the Login/Register pages ask first and
// simply don't render the button when it's off — no broken CTA, no crash.
// Cached hard: this only changes on a server redeploy.
export function useGoogleAuthEnabled() {
  return useQuery({
    queryKey: ['auth', 'google', 'status'],
    queryFn: () => api.request<ApiResponse<{ enabled: boolean }>>('/auth/google/status'),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
}
